import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Employee, EmployeeFilters } from '@/types/employee'
import { employeeService } from '@/services/user'
import { useAppStore } from './app'

export const useEmployeeStore = defineStore('employee', () => {
  const appStore = useAppStore()
  
  // Load filters from localStorage
  const loadFiltersFromStorage = (): EmployeeFilters => {
    try {
      const savedFilters = localStorage.getItem('employeeFilters')
      if (savedFilters) {
        return JSON.parse(savedFilters)
      }
    } catch (error) {
      console.error('Failed to load filters from localStorage:', error)
    }
    return {
      search: '',
      status: '',
      sortBy: '',
      sortDirection: 'asc'
    }
  }

  // Save filters to localStorage
  const saveFiltersToStorage = (filters: EmployeeFilters) => {
    try {
      localStorage.setItem('employeeFilters', JSON.stringify(filters))
    } catch (error) {
      console.error('Failed to save filters to localStorage:', error)
    }
  }
  
  // State
  const employees = ref<Employee[]>([])
  const selectedEmployee = ref<Employee | null>(null)
  const filters = ref<EmployeeFilters>(loadFiltersFromStorage())
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const filteredEmployees = computed(() => {
    let filtered = [...employees.value]

    // Filter out deleted employees by default (soft delete)
    if (filters.value.status !== 'Deleted') {
      filtered = filtered.filter(employee => employee.status !== 'Deleted')
    }

    // Search filter
    if (filters.value.search) {
      const searchTerm = filters.value.search.toLowerCase()
      filtered = filtered.filter(employee => 
        employee.first_name.toLowerCase().includes(searchTerm) ||
        employee.last_name.toLowerCase().includes(searchTerm) ||
        employee.email.toLowerCase().includes(searchTerm) ||
        (employee.middle_name || '').toLowerCase().includes(searchTerm)
      )
    }

    // Status filter
    if (filters.value.status) {
      filtered = filtered.filter(employee => employee.status === filters.value.status)
    }

    // Sorting
    if (filters.value.sortBy) {
      const sortKey = filters.value.sortBy
      filtered.sort((a, b) => {
        const aVal = (a as any)[sortKey]?.toString().toLowerCase() ?? ''
        const bVal = (b as any)[sortKey]?.toString().toLowerCase() ?? ''
        
        if (aVal < bVal) return filters.value.sortDirection === 'asc' ? -1 : 1
        if (aVal > bVal) return filters.value.sortDirection === 'asc' ? 1 : -1
        return 0
      })
    } else {
      // Default sorting: newest first (by created_at descending)
      filtered.sort((a, b) => {
        const aDate = new Date(a.created_at).getTime()
        const bDate = new Date(b.created_at).getTime()
        return bDate - aDate // Descending order (newest first)
      })
    }

    return filtered
  })

  const totalEmployees = computed(() => employees.value.filter(e => e.status !== 'Deleted').length)
  const activeEmployees = computed(() => employees.value.filter(e => e.status === 'Active').length)
  const inactiveEmployees = computed(() => employees.value.filter(e => e.status === 'Inactive').length)

  // Actions
  async function fetchEmployees() {
    try {
      isLoading.value = true
      error.value = null
      const data = await employeeService.getEmployees()
      employees.value = data
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch employees'
      appStore.setError(error.value)
    } finally {
      isLoading.value = false
    }
  }

  async function fetchEmployee(id: number) {
    try {
      isLoading.value = true
      error.value = null
      const employee = await employeeService.getEmployee(id)
      selectedEmployee.value = employee
      return employee
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch employee'
      appStore.setError(error.value)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function updateEmployeeStatus(id: number, status: boolean) {
    try {
      isLoading.value = true
      error.value = null
      
      // For now, just update locally without API call
      console.log('Update employee status - Not implemented yet', { id, status })
      
      // Simulate updating the employee in the local state
      const index = employees.value.findIndex(e => e.user_details_id === id)
      if (index !== -1) {
        employees.value[index].status = status ? 'Active' : 'Inactive'
      }
      
      return employees.value[index]
    } catch (err: any) {
      error.value = err.message || 'Failed to update employee status'
      appStore.setError(error.value)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function deleteEmployee(id: number) {
    try {
      isLoading.value = true
      error.value = null
      
      // For now, just log without API call
      console.log('Delete employee - Not implemented yet', { id })
      
      // Don't actually remove from local state for now
    } catch (err: any) {
      error.value = err.message || 'Failed to delete employee'
      appStore.setError(error.value)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  function setFilters(newFilters: Partial<EmployeeFilters>) {
    filters.value = { ...filters.value, ...newFilters }
    saveFiltersToStorage(filters.value)
  }

  function clearFilters() {
    filters.value = {
      search: '',
      status: '',
      sortBy: '',
      sortDirection: 'asc'
    }
    saveFiltersToStorage(filters.value)
  }

  function setSorting(column: string) {
    if (filters.value.sortBy === column) {
      filters.value.sortDirection = filters.value.sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      filters.value.sortBy = column
      filters.value.sortDirection = 'asc'
    }
    saveFiltersToStorage(filters.value)
  }

  return {
    // State
    employees,
    selectedEmployee,
    filters,
    isLoading,
    error,
    
    // Getters
    filteredEmployees,
    totalEmployees,
    activeEmployees,
    inactiveEmployees,
    
    // Actions
    fetchEmployees,
    fetchEmployee,
    updateEmployeeStatus,
    deleteEmployee,
    setFilters,
    clearFilters,
    setSorting
  }
})
